import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Shell } from './components/Shell';
import { SignIn } from './screens/SignIn';
import { MainControl } from './screens/MainControl';
import { Projects } from './screens/Projects';
import { ProjectControl } from './screens/ProjectControl';
import { JourneyScreen } from './screens/JourneyScreen';
import { Crew } from './screens/Crew';
import { AgentOperations } from './screens/AgentOperations';
import { Alerts } from './screens/Alerts';
import { Integrations } from './screens/Integrations';
import { ConnectProject } from './screens/ConnectProject';
import { AuditScreen } from './screens/AuditScreen';
import { Forecast } from './screens/Forecast';
import { Control } from './screens/Control';
import { Journeys } from './screens/Journeys';
import { Accounts } from './screens/Accounts';
import { DemoFrame } from './components/DemoFrame';
import { SetPassword } from './screens/SetPassword';
import { BridgeDoors } from './entrance/BridgeDoors';
import { useEntrance } from './entrance/useEntrance';
import { dashboardObscured, doorsPresent } from './entrance/machine';
import { useProjection } from './hooks/useProjection';
import { api } from './lib/api';

function Authenticated() {
  // The intervention count drives the navigation badge on every screen, so it
  // is loaded once at the shell level rather than per screen.
  const counts = useProjection(() => api.portfolio(), []);
  const interventions = counts.data?.interventions ?? [];
  const open = interventions.length;
  const critical = interventions.filter(
    (i) => i.severity === 'CRITICAL' || i.severity === 'BLOCKER',
  ).length;
  return (
    <Shell counts={{ interventions: open, critical }}>
      <Routes>
        <Route path="/" element={<MainControl />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<Navigate to="control" replace />} />
        <Route path="/projects/:projectId/control" element={<ProjectControl />} />
        <Route path="/projects/:projectId/journey" element={<JourneyScreen />} />
        <Route path="/projects/:projectId/connections" element={<Integrations />} />
        <Route path="/crew" element={<Crew />} />
        <Route path="/agents" element={<AgentOperations />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/integrations/connect" element={<ConnectProject />} />
        <Route path="/forecast" element={<Forecast />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/audit" element={<AuditScreen />} />
        <Route path="/journeys" element={<Journeys />} />
        <Route path="/control" element={<Control interventions={open} critical={critical} />} />
        {/* Demo mode. The same Main Control screen over a portfolio built in
            the browser: no request, nothing to write, and framed so it cannot
            be read as live. */}
        <Route
          path="/demo"
          element={
            <DemoFrame>
              <MainControl source="demo" />
            </DemoFrame>
          }
        />
        {/* The phone-only name for Control, kept so a bookmark still lands. */}
        <Route path="/more" element={<Navigate to="/control" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export function App() {
  const { status } = useAuth();
  const location = useLocation();
  const entrance = useEntrance(status);

  if (status === 'checking') {
    return (
      <main className="main" aria-busy="true">
        <span className="sr-only">Checking your session</span>
        <div className="skeleton skeleton--title" />
        <div className="skeleton skeleton--block" />
      </main>
    );
  }

  // Setting a password is reachable without a session by necessity: the holder
  // of the link is precisely someone who cannot sign in yet. No doors: this is
  // not an arrival, it is an errand.
  if (location.pathname === '/set-password') return <SetPassword />;

  const signedIn = status === 'signed-in';
  const showDoors = doorsPresent(entrance.state);
  const obscured = dashboardObscured(entrance.state);

  return (
    <>
      {/*
        The authenticated application mounts the instant authentication lands,
        while the doors are still shut. By the time they begin to travel — two
        tenths of a second later — its data is already in flight, so what is
        revealed is a Bridge that was running before you got there, not one
        that starts assembling because you looked at it.
       */}
      {signedIn && (
        <div
          className="app-layer"
          // Mounted and loading, but neither announced nor tabbable while it
          // is behind a shut door. Both come back the moment it starts to
          // become visible.
          aria-hidden={obscured ? true : undefined}
          inert={obscured}
        >
          <Authenticated />
        </div>
      )}

      {showDoors && (
        <BridgeDoors
          state={entrance.state}
          muted={entrance.muted}
          onToggleMuted={entrance.toggleMuted}
        >
          {!signedIn && <SignIn onStarted={entrance.authStarted} onFailed={entrance.authFailed} />}
        </BridgeDoors>
      )}
    </>
  );
}
